import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { asideLinks2 } from "constants/asideLinks2";
import { translate } from "services/general";
import { Tooltip } from "antd";
import {
  HAS_PERMISSION,
  getAsideLinkPermissionId,
  hasAsideLinkPermission,
} from "services/user";
import PermissionDebugger from "components/shared/PermissionDebugger";

const SmSideBar = ({ isDark }) => {
  const [openParent, setOpenParent] = useState(null);
  const [openChild, setOpenChild] = useState(null);
  const sidebarRef = useRef(null);
  const { pathname } = useLocation();

  const handleParentClick = (i) => {
    setOpenParent(openParent === i ? null : i);
    setOpenChild(null);
  };

  const handleChildClick = (k) => {
    setOpenChild(openChild === k ? null : k);
  };

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let last = localStorage.getItem("user");
    const id = setInterval(() => {
      const now = localStorage.getItem("user");
      if (now !== last) {
        last = now;
        setRefreshKey((k) => k + 1);
      }
    }, 300);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setOpenChild(null);
    setOpenParent(null);
  }, [pathname, refreshKey]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setOpenParent(null);
        setOpenChild(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <ul
      ref={sidebarRef}
      className={`w-20 sm-aside-sh pt-[119px] h-screen start-0 top-0 z-[99] fixed scrollbar-hidden flex flex-col items-center gap-6 ${
        isDark ? "bg-chrome" : "bg-surface"
      }`}
    >
      {asideLinks2.map(({ icon, path, children, text, en_text, id }, i) => {
        // ✅ Skip if user doesn't have permission for this link (or any of its children)
        const hasPermission = id
          ? HAS_PERMISSION(id)
          : children?.some(
              (child) =>
              hasAsideLinkPermission(child) ||
              child.children?.some((sub) => hasAsideLinkPermission(sub))
            );

        if (!hasPermission) return null;

        return (
          <React.Fragment key={path || text}>
            {path ? (
              id ? (
                <PermissionDebugger permissionId={id}>
                  <Tooltip color="#535353e1" title={translate(text, en_text)}>
                    <Link
                      to={path}
                      className={`block ${isDark ? "invert brightness-0" : ""}`}
                    >
                      {icon}
                    </Link>
                  </Tooltip>
                </PermissionDebugger>
              ) : (
                <Tooltip color="#535353e1" title={translate(text, en_text)}>
                  <Link
                    to={path}
                    className={`block ${isDark ? "invert brightness-0" : ""}`}
                  >
                    {icon}
                  </Link>
                </Tooltip>
              )
            ) : (
              <div className="relative">
                {id ? (
                  <PermissionDebugger permissionId={id}>
                    <Tooltip color="#535353e1" title={translate(text, en_text)}>
                      <button
                        onClick={() => handleParentClick(i)}
                        className={`block ${
                          isDark ? "invert brightness-0" : ""
                        }`}
                      >
                        {icon}
                      </button>
                    </Tooltip>
                  </PermissionDebugger>
                ) : (
                  <Tooltip color="#535353e1" title={translate(text, en_text)}>
                    <button
                      onClick={() => handleParentClick(i)}
                      className={`block ${isDark ? "invert brightness-0" : ""}`}
                    >
                      {icon}
                    </button>
                  </Tooltip>
                )}

                {/* Level 2 popup */}
                {openParent === i && children && (
                  <div
                    className={`absolute start-full top-1/2 w-[220px] -translate-y-1/2 ms-3 py-4 px-5 rounded-lg z-50 popup ${
                      isDark
                        ? "bg-[#E4E4E4] text-[#5A5A5A]"
                        : "bg-[#E4E4E4] text-[#5A5A5A]"
                    }`}
                  >
                    {/* Triangle */}
                    <div
                      className={`absolute -start-2 top-1/2 -translate-y-1/2 w-0 h-0 border-y-8 border-y-transparent border-e-8 ${
                        isDark ? "border-e-[#E4E4E4]" : "border-e-[#E4E4E4]"
                      }`}
                    ></div>

                    <ul className="flex flex-col gap-4">
                      {children
                        .filter(
                          (child) =>
                            hasAsideLinkPermission(child) ||
                            child.children?.some((sub) =>
                              hasAsideLinkPermission(sub)
                            )
                        )
                          .map((child, k) => {
                            const {
                              path,
                              text,
                              en_text,
                              children: subChildren,
                          } = child;
                          const childPermissionId =
                            getAsideLinkPermissionId(child);
                          return (
                            <li key={`${i}-${k}`} className="relative">
                              {subChildren ? (
                                <>
                                  {/* Clickable child to open sub-sub */}
                                  {childPermissionId ? (
                                    <PermissionDebugger
                                      permissionId={childPermissionId}
                                    >
                                      <button
                                        onClick={() => handleChildClick(k)}
                                        className="flex items-center gap-2 text-[15px] font-medium hover:underline w-full text-start"
                                      >
                                        <span>{translate(text, en_text)}</span>
                                      </button>
                                    </PermissionDebugger>
                                  ) : (
                                    <button
                                      onClick={() => handleChildClick(k)}
                                      className="flex items-center gap-2 text-[15px] font-medium hover:underline w-full text-start"
                                    >
                                      <span>{translate(text, en_text)}</span>
                                    </button>
                                  )}

                                  {/* Sub-sub popup */}
                                  {openChild === k && (
                                    <div
                                      className={`absolute start-full top-1/2 w-[220px] -translate-y-1/2 ms-7 py-4 px-5 rounded-lg z-50 popup ${
                                        isDark
                                          ? "bg-[#E4E4E4] text-[#5A5A5A]"
                                          : "bg-[#E4E4E4] text-[#5A5A5A]"
                                      }`}
                                    >
                                      {/* Triangle */}
                                      <div
                                        className={`absolute -start-2 top-1/2 -translate-y-1/2 w-0 h-0 border-y-8 border-y-transparent border-e-8 ${
                                          isDark
                                            ? "border-e-[#E4E4E4]"
                                            : "border-e-[#E4E4E4]"
                                        }`}
                                      ></div>

                                      <ul className="flex flex-col gap-3">
                                        {subChildren
                                          .filter((sub) =>
                                            hasAsideLinkPermission(sub)
                                          )
                                          .map(
                                            (
                                              {
                                                path,
                                                text,
                                                en_text,
                                                id: subId,
                                              },
                                              m
                                            ) => (
                                              <li key={`${i}-${k}-${m}`}>
                                                {subId ? (
                                                  <PermissionDebugger
                                                    permissionId={subId}
                                                  >
                                                    <Link
                                                      to={path}
                                                      className="text-[14px] font-medium hover:underline"
                                                      onClick={() => {
                                                        setOpenParent(null);
                                                        setOpenChild(null);
                                                      }}
                                                    >
                                                      {translate(text, en_text)}
                                                    </Link>
                                                  </PermissionDebugger>
                                                ) : (
                                                  <Link
                                                    to={path}
                                                    className="text-[14px] font-medium hover:underline"
                                                    onClick={() => {
                                                      setOpenParent(null);
                                                      setOpenChild(null);
                                                    }}
                                                  >
                                                    {translate(text, en_text)}
                                                  </Link>
                                                )}
                                              </li>
                                            )
                                          )}
                                      </ul>
                                    </div>
                                  )}
                                </>
                              ) : childPermissionId ? (
                                <PermissionDebugger
                                  permissionId={childPermissionId}
                                >
                                  <Link
                                    to={path}
                                    className="flex items-center gap-2 text-[15px] font-medium hover:underline"
                                    onClick={() => setOpenParent(null)}
                                  >
                                    {translate(text, en_text)}
                                  </Link>
                                </PermissionDebugger>
                              ) : (
                                <Link
                                  to={path}
                                  className="flex items-center gap-2 text-[15px] font-medium hover:underline"
                                  onClick={() => setOpenParent(null)}
                                >
                                  {translate(text, en_text)}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </ul>
  );
};

export default SmSideBar;
